import React from 'react';
import {
  Banner,
  Box,
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  Flex,
  Heading,
  LoadingOrbit,
  TagBadge,
  Text,
  TextArea,
  TextField,
  Checkbox,
  EmptyState,
} from '@chatsdet/ui';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import type { IssueCategorySetting } from '@chatsdet/types/issues';
import {
  useIssueCategoriesQuery,
  useCreateIssueCategoryMutation,
  useUpdateIssueCategoryMutation,
} from '../../../hooks/useIssueCategories.js';

type IssueCategoriesSectionProps = {
  isAdmin: boolean;
};

type CategoryFormState = {
  label: string;
  key: string;
  description: string;
  instructions: string;
  sortOrder: number;
  isActive: boolean;
};

const emptyForm = (sortOrder = 0): CategoryFormState => ({
  label: '',
  key: '',
  description: '',
  instructions: '',
  sortOrder,
  isActive: true,
});

export function IssueCategoriesSection({ isAdmin }: IssueCategoriesSectionProps) {
  const categoriesQuery = useIssueCategoriesQuery();
  const createMutation = useCreateIssueCategoryMutation();
  const updateMutation = useUpdateIssueCategoryMutation();

  const categories = categoriesQuery.data?.data ?? [];
  const [isCreateOpen, setIsCreateOpen] = React.useState(false);
  const [createForm, setCreateForm] = React.useState<CategoryFormState>(() =>
    emptyForm((categories.length + 1) * 10)
  );

  const [editingCategory, setEditingCategory] = React.useState<IssueCategorySetting | null>(null);
  const [editForm, setEditForm] = React.useState<CategoryFormState>(emptyForm());

  React.useEffect(() => {
    if (isCreateOpen) {
      createMutation.reset();
      setCreateForm(emptyForm((categories.length + 1) * 10));
    }
  }, [isCreateOpen, categories.length]);

  React.useEffect(() => {
    if (editingCategory) {
      updateMutation.reset();
      setEditForm({
        label: editingCategory.label,
        key: editingCategory.key,
        description: editingCategory.description ?? '',
        instructions: editingCategory.instructions ?? '',
        sortOrder: editingCategory.sortOrder,
        isActive: editingCategory.isActive,
      });
    }
  }, [editingCategory]);

  const handleCreate = () => {
    if (!createForm.label.trim()) return;
    createMutation.mutate(
      {
        label: createForm.label.trim(),
        key: createForm.key.trim() || undefined,
        description: createForm.description.trim() || undefined,
        instructions: createForm.instructions.trim() || undefined,
        sortOrder: Number.isFinite(createForm.sortOrder) ? createForm.sortOrder : undefined,
      },
      {
        onSuccess: () => {
          setIsCreateOpen(false);
        },
      }
    );
  };

  const handleUpdate = () => {
    if (!editingCategory) return;
    updateMutation.mutate(
      {
        id: editingCategory.id,
        label: editForm.label.trim() || undefined,
        key: editForm.key.trim() || undefined,
        description: editForm.description.trim() || null,
        instructions: editForm.instructions.trim() || null,
        sortOrder: editForm.sortOrder,
        isActive: editForm.isActive,
      },
      {
        onSuccess: () => {
          setEditingCategory(null);
        },
      }
    );
  };

  const sortedCategories = React.useMemo(
    () =>
      categories.slice().sort((a, b) => {
        if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
        return a.label.localeCompare(b.label);
      }),
    [categories]
  );

  const columns = React.useMemo<ColumnDef<StockFeatures, IssueCategorySetting>[]>(
    () => [
      {
        accessorKey: 'label',
        header: 'Label',
        cell: ({ row }) => <Text weight="medium">{row.original.label}</Text>,
      },
      {
        accessorKey: 'key',
        header: 'Key',
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">{row.original.key}</span>
        ),
      },
      {
        accessorKey: 'description',
        header: 'Description',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {row.original.description || '—'}
          </Text>
        ),
      },
      {
        accessorKey: 'isActive',
        header: 'Status',
        cell: ({ row }) => (
          <TagBadge
            name={row.original.isActive ? 'Active' : 'Inactive'}
            color={row.original.isActive ? 'green' : 'gray'}
          />
        ),
      },
      {
        accessorKey: 'sortOrder',
        header: 'Sort',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {row.original.sortOrder}
          </Text>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <Button
            variant="outline"
            size="sm"
            onPress={() => setEditingCategory(row.original)}
            isDisabled={!isAdmin}
          >
            Edit
          </Button>
        ),
      },
    ],
    [isAdmin]
  );

  return (
    <Box className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <Heading level={2}>Issue Labels</Heading>
          <Text size="sm" className="text-muted-foreground">
            Configure the categories agents should use when reporting bugs.
          </Text>
        </div>
        <DialogTrigger isOpen={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <Button isDisabled={!isAdmin}>Add Category</Button>
          <Dialog title="Add Issue Category">
            <DialogContent>
              <Box className="space-y-4">
                <TextField
                  label="Label"
                  value={createForm.label}
                  onChange={(value) => setCreateForm((prev) => ({ ...prev, label: value }))}
                  isRequired
                />
                <TextField
                  label="Key (optional)"
                  description="Lowercase identifier agents will reference"
                  value={createForm.key}
                  onChange={(value) => setCreateForm((prev) => ({ ...prev, key: value }))}
                />
                <TextArea
                  label="Description"
                  rows={3}
                  value={createForm.description}
                  onChange={(value) => setCreateForm((prev) => ({ ...prev, description: value }))}
                />
                <TextArea
                  label="Agent Instructions"
                  rows={3}
                  value={createForm.instructions}
                  onChange={(value) => setCreateForm((prev) => ({ ...prev, instructions: value }))}
                />
                <TextField
                  label="Sort order"
                  type="number"
                  value={String(createForm.sortOrder)}
                  onChange={(value) =>
                    setCreateForm((prev) => ({ ...prev, sortOrder: Number(value) || 0 }))
                  }
                />
              </Box>
              {createMutation.isError && (
                <Banner variant="error">{createMutation.error.message}</Banner>
              )}
              <DialogFooter>
                <Button variant="outline" onPress={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onPress={handleCreate}
                  isDisabled={!createForm.label.trim() || createMutation.isPending}
                >
                  {createMutation.isPending ? 'Saving...' : 'Save'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </DialogTrigger>
      </div>

      {categoriesQuery.isLoading ? (
        <Flex align="center" justify="center" className="py-12">
          <LoadingOrbit size="md" />
        </Flex>
      ) : categories.length === 0 ? (
        <EmptyState
          title="No categories configured"
          description="Use the Add Category button to seed your issue labels."
        />
      ) : (
        <DataTable columns={columns} data={sortedCategories} aria-label="Issue categories table" />
      )}

      <DialogTrigger
        isOpen={Boolean(editingCategory)}
        onOpenChange={(open) => !open && setEditingCategory(null)}
      >
        <span />
        <Dialog title="Edit Issue Category">
          <DialogContent>
            <Box className="space-y-4">
              <TextField
                label="Label"
                value={editForm.label}
                onChange={(value) => setEditForm((prev) => ({ ...prev, label: value }))}
                isRequired
              />
              <TextField
                label="Key"
                value={editForm.key}
                onChange={(value) => setEditForm((prev) => ({ ...prev, key: value }))}
              />
              <TextArea
                label="Description"
                rows={3}
                value={editForm.description}
                onChange={(value) => setEditForm((prev) => ({ ...prev, description: value }))}
              />
              <TextArea
                label="Agent Instructions"
                rows={3}
                value={editForm.instructions}
                onChange={(value) => setEditForm((prev) => ({ ...prev, instructions: value }))}
              />
              <TextField
                label="Sort order"
                type="number"
                value={String(editForm.sortOrder)}
                onChange={(value) =>
                  setEditForm((prev) => ({ ...prev, sortOrder: Number(value) || 0 }))
                }
              />
              <Checkbox
                isSelected={editForm.isActive}
                onChange={(isSelected) =>
                  setEditForm((prev) => ({ ...prev, isActive: isSelected }))
                }
                label="Active"
              />
            </Box>
            {updateMutation.isError && (
              <Banner variant="error">{updateMutation.error.message}</Banner>
            )}
            <DialogFooter>
              <Button variant="outline" onPress={() => setEditingCategory(null)}>
                Cancel
              </Button>
              <Button
                onPress={handleUpdate}
                isDisabled={!editForm.label.trim() || updateMutation.isPending}
              >
                {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogTrigger>
    </Box>
  );
}
